import { Box, Flex, Input, Textarea } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { FileInput } from '../../../components/FileInput'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { MaskedInput } from '../../../components/MaskedInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { JobApplicationService } from '../../../services/JobApplication/JobApplicationService'
import {
  JobApplicationComplete,
  JobApplicationRequisitionOption,
  JobApplicationStatuses,
  SalaryRange
} from '../../../services/JobApplication/types'
import { JOB_REQUISITION_STATUS } from '../JobRequisitions/constants'
import { statusInfo } from './JobApplicationPage'

interface IProps {
  isOpen: boolean
  onClose: () => void
  /** RPs that went live; the form offers the open ones and the application's current one. */
  requisitions: JobApplicationRequisitionOption[]
  jobApplication?: JobApplicationComplete
  salaryRanges: SalaryRange[]
}

export interface IJobApplicationFields {
  name: string
  email: string
  phone: string
  job_requisition_id: number | null
  salary_range_id: string
  portfolio_link: string
  status: JobApplicationStatuses
  area: string[]
  why_you_want_to_work_here: string
  reasons_to_hire_you: string
  resume: File | null
}

const baseForm: IJobApplicationFields = {
  name: '',
  email: '',
  phone: '',
  job_requisition_id: null,
  salary_range_id: '',
  status: 'qualified',
  area: [],
  why_you_want_to_work_here: '',
  reasons_to_hire_you: '',
  resume: null,
  portfolio_link: ''
}

export function JobApplicationFormModal({ isOpen, onClose, requisitions, jobApplication, salaryRanges }: IProps) {
  const service = new JobApplicationService()
  const [saving, setSaving] = useState(false)
  const toast = useAppToast()
  const [form, setForm] = useState<IJobApplicationFields>(baseForm)
  // Laravel's `errors`: one or more messages per field (`area.0` included).
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({})
  // An application from before the RPs keeps its vacancy name until RH links it to an RP.
  const isLegacy = !!jobApplication && !jobApplication.job_requisition_id

  const requisitionOptions = useMemo(
    () =>
      requisitions.filter(
        (requisition) => requisition.status === 'open' || requisition.id === jobApplication?.job_requisition_id
      ),
    [requisitions, jobApplication?.job_requisition_id]
  )
  const selectedRequisition = requisitions.find((requisition) => requisition.id === form.job_requisition_id)
  // The site offers the areas of every open RP of the role, so a saved area may come from a sibling
  // RP: it stays offered while the application keeps its RP (the API accepts it the same way).
  const savedAreas =
    jobApplication && form.job_requisition_id === jobApplication.job_requisition_id ? (jobApplication.area ?? []) : []
  const areas = Array.from(new Set([...(selectedRequisition?.areas ?? []), ...savedAreas]))

  const errorsOf = (field: string) =>
    Object.entries(formErrors)
      .filter(([key]) => key === field || key.startsWith(`${field}.`))
      .flatMap(([, messages]) => messages)

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors({})
      setForm(
        jobApplication
          ? {
              name: jobApplication.name,
              email: jobApplication.email,
              phone: jobApplication.phone,
              job_requisition_id: jobApplication.job_requisition_id,
              status: jobApplication.status,
              area: jobApplication.area || [],
              salary_range_id: jobApplication.salary_range?.id.toString() || '',
              why_you_want_to_work_here: jobApplication.why_you_want_to_work_here || '',
              reasons_to_hire_you: jobApplication.reasons_to_hire_you || '',
              resume: null,
              portfolio_link: jobApplication.portfolio_link || ''
            }
          : baseForm
      )
    }
  }, [isOpen])

  /** Areas and portfolio follow the RP: what the new one doesn't offer is dropped. */
  const selectRequisition = (requisitionId: number | null) => {
    const requisition = requisitions.find((item) => item.id === requisitionId)
    setForm({
      ...form,
      job_requisition_id: requisitionId,
      area: form.area.filter((area) => requisition?.areas.includes(area)),
      portfolio_link: requisition?.allow_portfolio_link ? form.portfolio_link : ''
    })
  }

  const handleSave = async () => {
    setSaving(true)

    const saveFunction = jobApplication ? service.update(jobApplication.id, form) : service.create(form)

    saveFunction
      .then(() => {
        toast.success('Registro salvo com sucesso')
        onClose()
      })
      .catch((response) => {
        const status = response?.response?.status
        const errors = response?.response?.data?.errors

        if ((status === 400 || status === 422) && errors) {
          setFormErrors(errors)
          toast.invalidForm()
          return
        }

        toast.error(jobApplication ? 'Erro ao editar candidato' : 'Erro ao adicionar candidato', response)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${jobApplication ? 'Editar' : 'Registrar'} candidato`}
      onSubmit={handleSave}
      size="wide"
      isLoading={saving}
      isConfirmDisabled={saving || (!jobApplication && !form.resume)}
    >
      <Flex direction="column" gap={3}>
        <Flex direction="row" gap={3}>
          <Box w="75%">
            <FormField label="Nome" htmlFor="name" error={formErrors.name} isRequired>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                id="name"
                disabled={saving}
              />
            </FormField>
          </Box>
          <Box w="25%">
            <FormField label="Status" htmlFor="status" error={formErrors.status}>
              <FormSelect
                id="status"
                isDisabled={saving}
                value={form.status}
                onChange={(value) => setForm({ ...form, status: value as JobApplicationStatuses })}
                options={Object.entries(statusInfo).map(([key, value]) => ({ value: key, label: value.label }))}
                hideEmptyOption
              />
            </FormField>
          </Box>
        </Flex>

        <Flex direction="row" gap={3}>
          <Box w="40%">
            <FormField label="Telefone" error={formErrors.phone} isRequired>
              <MaskedInput
                mask="phone"
                value={form.phone}
                onChange={(phone) => setForm({ ...form, phone })}
                isDisabled={saving}
              />
            </FormField>
          </Box>

          <Box w="60%">
            <FormField label="E-mail" htmlFor="email" error={formErrors.email} isRequired>
              <Input
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                id="email"
                disabled={saving}
              />
            </FormField>
          </Box>
        </Flex>

        <Box>
          <FormField
            label="Vaga (RP)"
            error={errorsOf('job_requisition_id')}
            helperText={
              isLegacy
                ? `Candidatura anterior às RPs, para a vaga "${jobApplication?.job_position}". Escolha uma RP em aberto para ligá-la.`
                : 'RPs em aberto.'
            }
            isRequired={!isLegacy}
          >
            <SearchableSelect<number>
              options={requisitionOptions.map((requisition) => ({
                value: requisition.id,
                label:
                  `RP ${requisition.number} · ${requisition.name}` +
                  (requisition.status === 'open' ? '' : ` (${JOB_REQUISITION_STATUS[requisition.status].label})`)
              }))}
              value={form.job_requisition_id ?? ''}
              onChange={(requisitionId) => selectRequisition(requisitionId === '' ? null : requisitionId)}
              isClearable={isLegacy}
              placeholder="Selecione a RP"
              entity="RP"
              feminine
              isDisabled={saving}
            />
          </FormField>
        </Box>

        <Box>
          <FormField label="Pretensão salarial" htmlFor="salary_range" error={formErrors.salary_range_id} isRequired>
            <FormSelect
              id="salary_range"
              isDisabled={saving}
              value={form.salary_range_id}
              onChange={(value) => setForm({ ...form, salary_range_id: value })}
              options={salaryRanges.map((salaryRange) => ({ value: salaryRange.id, label: salaryRange.range }))}
              placeholder="Selecione uma pretensão salarial"
            />
          </FormField>
        </Box>

        {(selectedRequisition?.allow_portfolio_link ||
          (isLegacy && !form.job_requisition_id && form.portfolio_link)) && (
          <Box>
            <FormField
              label="Link de portfolio"
              htmlFor="portfolio_link"
              error={formErrors.portfolio_link}
              isRequired={!!selectedRequisition?.allow_portfolio_link}
            >
              <Input
                id="portfolio_link"
                value={form.portfolio_link}
                onChange={(e) => setForm({ ...form, portfolio_link: e.target.value })}
                disabled={saving}
              />
            </FormField>
          </Box>
        )}

        {areas.length > 0 && (
          <Box>
            <FormField label="Área" error={errorsOf('area')}>
              <SearchableSelect
                isMulti
                value={form.area ?? []}
                options={areas.map((area) => ({ label: area, value: area }))}
                onChange={(selectedAreas) => setForm({ ...form, area: selectedAreas })}
                placeholder="Selecione uma ou mais áreas"
                entity="área"
                feminine
                isDisabled={saving}
              />
            </FormField>
          </Box>
        )}

        <FileInput
          accept="document"
          label="Currículo"
          value={form.resume}
          onChange={(resume) => setForm({ ...form, resume })}
          error={formErrors.resume}
          isRequired={!jobApplication}
          isDisabled={saving}
        />

        <Box>
          <FormField
            label="Por que você quer trabalhar aqui?"
            htmlFor="why_you_want_to_work_here"
            error={formErrors.why_you_want_to_work_here}
          >
            <Textarea
              value={form.why_you_want_to_work_here}
              disabled={saving}
              height="110px"
              onChange={(e) => setForm({ ...form, why_you_want_to_work_here: e.target.value })}
            />
          </FormField>
        </Box>

        <Box>
          <FormField
            label="Razões para te contratar"
            htmlFor="reasons_to_hire_you"
            error={formErrors.reasons_to_hire_you}
          >
            <Textarea
              value={form.reasons_to_hire_you}
              disabled={saving}
              height="110px"
              onChange={(e) => setForm({ ...form, reasons_to_hire_you: e.target.value })}
            />
          </FormField>
        </Box>
      </Flex>
    </FormModal>
  )
}
